import React from 'react';
import { appDate } from '../../../../utils/appDateFormat';

// Simple inline stubs for UI components
const Icon = ({ name, size = 16, className = '' }) => {
  const sizeStr = size + 'px';
  return <span className={className} style={{ display: 'inline-flex', width: sizeStr, height: sizeStr }} />;
};
const Button = ({ children, onClick, variant, disabled, className = '', size: btnSize, ...rest }) => (
  <button onClick={onClick} disabled={disabled} className={`px-4 py-2 rounded-lg font-medium transition-colors ${variant === 'outline' ? 'border border-border bg-card text-gray-700 hover:bg-gray-50' : 'bg-blue-600 text-white hover:bg-blue-700'} ${btnSize === 'sm' ? 'px-3 py-1.5 text-sm' : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`} {...rest}>{children}</button>
);

const ProjectCard = ({ project, needsAttention, onView, onEdit, onDelete }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Upcoming':
        return 'bg-blue-100 text-blue-800';
      case 'Completed':
        return 'bg-gray-100 text-gray-800';
      case 'On Hold':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getHealthConfig = (health) => {
    switch (health) {
      case 'On Track':
        return { color: 'text-green-600', bg: 'bg-green-50', icon: 'CheckCircle', borderColor: 'border-green-500' };
      case 'At Risk':
        return { color: 'text-amber-600', bg: 'bg-amber-50', icon: 'AlertCircle', borderColor: 'border-amber-500' };
      case 'Blocked':
        return { color: 'text-red-600', bg: 'bg-red-50', icon: 'XCircle', borderColor: 'border-red-500' };
      default:
        return { color: 'text-gray-600', bg: 'bg-gray-50', icon: 'Circle', borderColor: 'border-gray-500' };
    }
  };

  const formatDate = (dateString) => {
    return appDate(dateString);
  };

  const isOverdue = new Date(project?.dateRange?.end) < new Date() && project?.status === 'Active';
  const progressPercentage = Math.round((project?.completedTasks / project?.taskCount) * 100) || 0;
  const healthConfig = getHealthConfig(project?.health);

  return (
    <div
      className={`
        bg-white border-2 rounded-lg p-6 hover:shadow-lg transition-all duration-200 group relative
        ${needsAttention
          ? 'border-l-4 border-l-amber-500 border-t-amber-100 border-r-amber-100 border-b-amber-100 shadow-md'
          : 'border-border hover:border-primary/30'
        }
      `}
    >
      {/* Needs Attention Badge */}
      {needsAttention && (
        <div className="absolute top-4 right-4">
          <div className="flex items-center bg-amber-100 text-amber-800 px-2 py-1 rounded-full text-xs font-semibold">
            <Icon name="AlertTriangle" size={12} className="mr-1" />
            Needs Attention
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 pr-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-primary transition-colors duration-200">
            {project?.name}
          </h3>
          <p className="text-xs text-gray-500">ID: {project?.id}</p>
        </div>
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
          {project?.status}
        </span>
      </div>

      {/* Health Indicator */}
      <div className={`flex items-center justify-between p-3 rounded-lg mb-4 ${healthConfig?.bg}`}>
        <div className="flex items-center space-x-2">
          <Icon name={healthConfig?.icon} size={18} className={healthConfig?.color} />
          <span className={`text-sm font-semibold ${healthConfig?.color}`}>
            {project?.health}
          </span>
        </div>
        <div className="text-right">
          <div className="text-xs text-gray-600">Progress</div>
          <div className={`text-sm font-bold ${healthConfig?.color}`}>{progressPercentage}%</div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
          <span>Completion</span>
          <span>{project?.completedTasks} / {project?.taskCount} tasks</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${
              project?.health === 'On Track' ? 'bg-green-500' :
              project?.health === 'At Risk'? 'bg-amber-500' : 'bg-red-500'
            }`}
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Task Summary */}
      <div className="grid grid-cols-2 gap-3 mb-4 pb-4 border-b border-border">
        <div className="flex items-center space-x-2">
          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="CheckSquare" size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-xs text-gray-500">Total Tasks</p>
            <p className="text-lg font-semibold text-gray-900">{project?.taskCount}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            project?.overdueTasks > 0 ? 'bg-red-50' : 'bg-gray-50'
          }`}>
            <Icon
              name="Clock"
              size={18}
              className={project?.overdueTasks > 0 ? 'text-red-600' : 'text-gray-400'}
            />
          </div>
          <div>
            <p className="text-xs text-gray-500">Overdue</p>
            <p className={`text-lg font-semibold ${
              project?.overdueTasks > 0 ? 'text-red-600' : 'text-gray-900'
            }`}>
              {project?.overdueTasks}
            </p>
          </div>
        </div>
      </div>

      {/* Ownership */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-semibold text-sm">
            {project?.owner?.initials}
          </div>
          <div>
            <p className="text-xs text-gray-500">Project Owner</p>
            <p className="text-sm font-medium text-gray-900">{project?.owner?.name}</p>
          </div>
        </div>
        <div className="flex items-center space-x-1 text-gray-600">
          <Icon name="Users" size={16} />
          <span className="text-sm font-medium">{project?.teamMembers}</span>
        </div>
      </div>

      {/* Date Range with Overdue Warning */}
      <div className="mb-4">
        <div className="flex items-center text-sm text-gray-600 mb-1">
          <Icon name="CalendarDays" size={14} className="mr-2 text-gray-400" />
          <span className="font-medium">Timeline</span>
        </div>
        <div className="flex items-center justify-between ml-6">
          <p className="text-sm text-gray-900">
            {formatDate(project?.dateRange?.start)} - {formatDate(project?.dateRange?.end)}
          </p>
          {isOverdue && (
            <div className="flex items-center text-red-600 text-xs font-semibold">
              <Icon name="AlertCircle" size={12} className="mr-1" />
              Overdue
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-2 gap-2">
        <Button
          onClick={onView}
          variant="default"
          size="sm"
          className="w-full"
        >
          <Icon name="Eye" size={14} className="mr-1" />
          View
        </Button>
        <Button
          onClick={onEdit}
          variant="outline"
          size="sm"
          className="w-full"
        >
          <Icon name="Edit" size={14} className="mr-1" />
          Edit
        </Button>
      </div>
      <div className="mt-2">
        <Button
          onClick={onDelete}
          variant="outline"
          size="sm"
          className="w-full text-red-600 hover:bg-red-50 hover:border-red-300"
        >
          <Icon name="Archive" size={14} className="mr-1" />
          Archive Project
        </Button>
      </div>
    </div>
  );
};

export default ProjectCard;
